import { useState } from "react";
import { useNavigate } from "react-router";
import {
  Archive,
  ArchiveRestore,
  Globe,
  Loader2,
  Trash2,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import type { PartnerGoal, PartnerGoalTarget } from "@colab/shared";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useArchiveGoal } from "./use-archive-goal";
import { useDeleteGoal } from "./use-delete-goal";
import { useUpdateGoal } from "./use-update-goal";
import { GoalDefinitionPane } from "./goal-definition-pane";
import { GoalPreviewPane } from "./goal-preview-pane";
import { GoalTargetsEditor } from "./goal-targets-editor";

type Tab = "definition" | "targets";

export function GoalEditor({
  goal,
  targets,
  partners,
  canManage,
}: {
  goal: PartnerGoal;
  targets: PartnerGoalTarget[];
  partners: Array<{ id: string; company_name: string }>;
  canManage: boolean;
}) {
  const [tab, setTab] = useState<Tab>("definition");
  // Bump to re-trigger the preview pane fetch after a definition save.
  const [previewSeed, setPreviewSeed] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const archiveMutation = useArchiveGoal();
  const updateMutation = useUpdateGoal();
  const deleteMutation = useDeleteGoal();
  const navigate = useNavigate();

  function onToggleArchive() {
    if (goal.is_archived) {
      updateMutation.mutate(
        {
          id: goal.id,
          partnerId: goal.partner_id,
          patch: { is_archived: false },
        },
        { onSuccess: () => toast.success("Goal restored") },
      );
    } else {
      archiveMutation.mutate(
        { id: goal.id, partnerId: goal.partner_id },
        { onSuccess: () => toast.success("Goal archived") },
      );
    }
  }

  function onConfirmDelete() {
    deleteMutation.mutate(
      { id: goal.id, partnerId: goal.partner_id },
      {
        onSuccess: () => {
          toast.success("Goal deleted");
          navigate("/admin/goals");
        },
      },
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl font-semibold tracking-tight">
              {goal.title}
            </h1>
            {goal.partner_id ? (
              <Badge variant="outline" className="gap-1 text-muted-foreground">
                <Users className="h-3 w-3" /> Partner-scoped
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1 text-muted-foreground">
                <Globe className="h-3 w-3" /> Workspace
              </Badge>
            )}
            {goal.is_archived && (
              <Badge variant="outline" className="gap-1 text-muted-foreground">
                <Archive className="h-3 w-3" /> Archived
              </Badge>
            )}
          </div>
          {goal.description && (
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              {goal.description}
            </p>
          )}
        </div>
        {canManage && (
          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onToggleArchive}
              disabled={archiveMutation.isPending || updateMutation.isPending}
            >
              {goal.is_archived ? (
                <>
                  <ArchiveRestore className="mr-1.5 h-3.5 w-3.5" /> Restore
                </>
              ) : (
                <>
                  <Archive className="mr-1.5 h-3.5 w-3.5" /> Archive
                </>
              )}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => setConfirmDelete(true)}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
            </Button>
          </div>
        )}
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this goal?</AlertDialogTitle>
            <AlertDialogDescription>
              The goal and all of its targets are removed. This can&apos;t be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              onClick={(e) => {
                e.preventDefault();
                onConfirmDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="mb-6 flex items-center gap-1 border-b">
        <TabButton
          active={tab === "definition"}
          onClick={() => setTab("definition")}
        >
          Definition
        </TabButton>
        <TabButton active={tab === "targets"} onClick={() => setTab("targets")}>
          Targets
          <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-muted px-1.5 text-[10px] tabular-nums text-muted-foreground">
            {targets.length}
          </span>
        </TabButton>
      </div>

      {tab === "definition" ? (
        <div className="grid gap-6 lg:grid-cols-10">
          <div className="lg:col-span-7">
            <GoalDefinitionPane
              goal={goal}
              partners={partners}
              canManage={canManage}
              onSpecChange={() => setPreviewSeed((n) => n + 1)}
            />
          </div>
          <div className="lg:col-span-3">
            <GoalPreviewPane goal={goal} seed={previewSeed} />
          </div>
        </div>
      ) : (
        <GoalTargetsEditor
          goal={goal}
          targets={targets}
          partners={partners}
          canManage={canManage}
        />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "relative inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors " +
        (active
          ? "text-foreground"
          : "text-muted-foreground hover:text-foreground")
      }
    >
      {children}
      {active && (
        <span className="absolute -bottom-px left-3 right-3 h-px bg-foreground" />
      )}
    </button>
  );
}
